/* ============================================
   GARMENT ARCHIVE — Experience Engine
   Shopify 2.0 Theme
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initLoadingScreen();
  ShopifyCart.updateCount();
});

function initSiteAfterLoad() {
  initThreshold();
  initNavBar();
  initScrollReveal();
  initCursorLight();
  initProductCards();
  initInspectionModal();
  initFilterBar();
  initShippingCards();
  initMobileNav();
  initConstellationView();
  initGravimetricScroll();
  initDustParticles();
  initDeadStockAlert();
  initAuthDuel();
  initSmoothScroll();
  initCart();
  initButtonHandlers();
  initProductForms();
  initCartPage();
}

/* ============================================
   SHOPIFY CART SYSTEM
   ============================================ */
const ShopifyCart = {
  async add(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    const data = await res.json();
    this.updateCount();
    this.showNotification(data.title || 'Item added to archive');
    return data;
  },

  async update(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    const data = await res.json();
    this.updateCount();
    return data;
  },

  async remove(key) {
    return this.update(key, 0);
  },

  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async updateCount() {
    try {
      const cart = await this.get();
      const badges = document.querySelectorAll('.cart-count');
      badges.forEach(badge => {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
    } catch (e) {
      // Silently fail if not on Shopify (e.g., local dev)
    }
  },

  showNotification(title) {
    const notif = document.createElement('div');
    notif.className = 'cart-notification';
    notif.innerHTML = '<span class="cart-notif-dot"></span>' + title + ' added to your archive';
    document.body.appendChild(notif);
    requestAnimationFrame(() => notif.classList.add('visible'));
    setTimeout(() => {
      notif.classList.remove('visible');
      setTimeout(() => notif.remove(), 500);
    }, 3000);
  }
};

/* ============================================
   PRODUCT FORM HANDLING (Add to Cart)
   ============================================ */
function initProductForms() {
  document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const id = form.querySelector('[name="id"]').value;
      const qty = form.querySelector('[name="quantity"]')?.value || 1;
      const btn = form.querySelector('button[type="submit"]');
      const originalText = btn.textContent;
      btn.textContent = 'ADDING...';
      btn.disabled = true;
      try {
        await ShopifyCart.add(parseInt(id), parseInt(qty));
        btn.textContent = 'ADDED TO ARCHIVE';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      } catch (err) {
        btn.textContent = 'ERROR';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      }
    });
  });
}

/* ============================================
   CART PAGE QUANTITY CONTROLS
   ============================================ */
function initCartPage() {
  document.querySelectorAll('.cart-qty-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const input = btn.parentElement.querySelector('.cart-qty-input');
      let qty = parseInt(input.value);
      if (action === 'plus') qty++;
      if (action === 'minus') qty = Math.max(0, qty - 1);
      input.value = qty;
      if (qty === 0) {
        btn.closest('.cart-item--page').style.opacity = '0.3';
      }
      await ShopifyCart.update(key, qty);
      if (qty === 0) location.reload();
      else updateCartTotals();
    });
  });

  document.querySelectorAll('.cart-remove-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      const item = btn.closest('.cart-item--page');
      if (item) item.style.opacity = '0.3';
      await ShopifyCart.remove(key);
      location.reload();
    });
  });
}

function updateCartTotals() {
  // Re-fetch cart and update subtotal display
  ShopifyCart.get().then(cart => {
    const subtotalEl = document.querySelector('.cart-subtotal-value');
    if (subtotalEl) {
      subtotalEl.textContent = (cart.total_price / 100).toLocaleString('en-ZA', {
        style: 'currency',
        currency: cart.currency || 'ZAR'
      });
    }
  });
}

/* ============================================
   VAULT DOOR LOADING SCREEN
   ============================================ */
function initLoadingScreen() {
  const screen = document.getElementById('loadingScreen');
  const bar = document.getElementById('loadingBar');
  const status = document.getElementById('loadingStatus');
  const lock = document.getElementById('vaultLock');
  if (!screen) { initSiteAfterLoad(); return; }

  const messages = [
    'VERIFYING CREDENTIALS...',
    'DECRYPTING ARCHIVE...',
    'SCANNING REGISTRY...',
    'ACCESS GRANTED'
  ];

  let progress = 0;
  let msgIndex = 0;

  const interval = setInterval(() => {
    progress += Math.random() * 12 + 4;
    if (progress > 100) progress = 100;

    if (bar) bar.style.width = progress + '%';

    const nextThreshold = (msgIndex + 1) * (100 / messages.length);
    if (progress > nextThreshold && msgIndex < messages.length - 1) {
      msgIndex++;
      if (status) status.textContent = messages[msgIndex];
    }

    if (progress >= 100) {
      clearInterval(interval);
      if (status) status.textContent = messages[messages.length - 1];

      // Stop the lock rotation
      if (lock) lock.classList.add('stopped');

      setTimeout(() => {
        // Open the vault doors
        screen.classList.add('dismissed');
        document.body.style.overflow = '';
        initSiteAfterLoad();

        setTimeout(() => {
          screen.classList.add('fade-out');
          setTimeout(() => {
            screen.style.display = 'none';
          }, 600);
        }, 1200);
      }, 600);
    }
  }, 200);

  document.body.style.overflow = 'hidden';
}

/* ============================================
   MOMENT 1: THE THRESHOLD
   ============================================ */
function initThreshold() {
  const hairline = document.querySelector('.threshold-hairline');
  const light = document.querySelector('.threshold-light');
  const wordmark = document.querySelector('.threshold-wordmark');
  const tagline = document.querySelector('.threshold-tagline');
  const navPlates = document.querySelector('.nav-plates');

  setTimeout(() => {
    if (hairline) hairline.classList.add('revealed');
  }, 500);

  setTimeout(() => {
    if (light) light.classList.add('revealed');
    if (wordmark) wordmark.classList.add('revealed');
  }, 1500);

  setTimeout(() => {
    if (tagline) tagline.classList.add('revealed');
  }, 2500);

  setTimeout(() => {
    if (navPlates) navPlates.classList.add('revealed');
  }, 3500);
}

/* ============================================
   STICKY NAVIGATION BAR
   ============================================ */
function initNavBar() {
  const navBar = document.querySelector('.nav-bar');
  const threshold = document.getElementById('threshold');

  if (!navBar) return;

  // If there's no threshold (non-homepage), show nav immediately
  if (!threshold) {
    navBar.classList.add('visible');
    return;
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      navBar.classList.toggle('visible', !entry.isIntersecting);
    },
    { threshold: 0.1 }
  );

  observer.observe(threshold);
}

/* ============================================
   SCROLL REVEAL (data-animate system)
   ============================================ */
function initScrollReveal() {
  const elements = document.querySelectorAll('[data-animate]');

  elements.forEach(el => {
    el.setAttribute('data-animate', 'hidden');
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = parseInt(entry.target.dataset.delay || '0', 10) * 100;
          setTimeout(() => {
            entry.target.setAttribute('data-animate', 'visible');
          }, delay);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.05, rootMargin: '0px 0px 80px 0px' }
  );

  elements.forEach(el => observer.observe(el));
}

/* ============================================
   CURSOR LIGHT EFFECT
   ============================================ */
function initCursorLight() {
  const light = document.querySelector('.cursor-light');
  if (!light) return;

  let mouseX = 0, mouseY = 0;
  let lightX = 0, lightY = 0;

  document.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  });

  function animate() {
    lightX += (mouseX - lightX) * 0.08;
    lightY += (mouseY - lightY) * 0.08;
    light.style.left = lightX + 'px';
    light.style.top = lightY + 'px';
    requestAnimationFrame(animate);
  }

  animate();
}

/* ============================================
   PRODUCT CARDS INTERACTION
   ============================================ */
function initProductCards() {
  const cards = document.querySelectorAll('.product-card');

  cards.forEach((card, index) => {
    // If card has an <a> link wrapping it, let that handle navigation
    // Only attach click-to-inspect for cards without links
    const link = card.querySelector('.product-card-link');
    if (!link) {
      const imageArea = card.querySelector('.product-card-image');
      const infoArea = card.querySelector('.product-card-info');
      if (imageArea) imageArea.addEventListener('click', () => openInspection(card));
      if (infoArea) infoArea.addEventListener('click', () => openInspection(card));
    }

    // Add "days in archive" element
    const info = card.querySelector('.product-card-info');
    if (info && !info.querySelector('.product-card-archive-days')) {
      const daysEl = document.createElement('div');
      daysEl.className = 'product-card-archive-days';
      const seed = ((index * 7 + 13) * 31) % 180 + 1;
      daysEl.textContent = 'IN ARCHIVE: ' + seed + ' DAYS';
      info.appendChild(daysEl);
    }

    // Add light-follow element to the card image area
    const imageArea = card.querySelector('.product-card-image');
    if (imageArea && !imageArea.querySelector('.product-card-light-follow')) {
      const lightFollow = document.createElement('div');
      lightFollow.className = 'product-card-light-follow';
      imageArea.appendChild(lightFollow);

      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        lightFollow.style.background = 'radial-gradient(circle 120px at ' + x + 'px ' + y + 'px, rgba(201,169,78,0.12), transparent 70%)';
      });
    }
  });
}

/* ============================================
   INSPECTION MODAL
   ============================================ */
function initInspectionModal() {
  const overlay = document.querySelector('.inspection-overlay');
  const closeBtn = document.querySelector('.inspection-close');
  const acquireBtn = overlay ? overlay.querySelector('.btn-acquire') : null;

  if (!overlay || !closeBtn) return;

  closeBtn.addEventListener('click', closeInspection);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeInspection();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeInspection();
  });

  if (acquireBtn) {
    acquireBtn.addEventListener('click', () => {
      const brand = overlay.querySelector('.inspection-brand')?.textContent || '';
      const name = overlay.querySelector('.inspection-name')?.textContent || '';
      const price = overlay.querySelector('.inspection-price')?.textContent || '';
      const imgSrc = overlay.querySelector('.inspection-image img')?.src || '';
      const id = 'insp-' + brand + '-' + name;
      addToCart({ brand, name, price, imgSrc, id });
      closeInspection();
      openCartDrawer();
    });
  }
}

function openInspection(card) {
  const overlay = document.querySelector('.inspection-overlay');
  if (!overlay) return;

  const brand = card.querySelector('.product-card-brand')?.textContent || '';
  const name = card.querySelector('.product-card-name')?.textContent || '';
  const price = card.querySelector('.product-card-price')?.textContent || '';
  const imgSrc = card.querySelector('.product-card-image img')?.src || '';

  const inspBrand = overlay.querySelector('.inspection-brand');
  const inspName = overlay.querySelector('.inspection-name');
  const inspPrice = overlay.querySelector('.inspection-price');
  const inspImg = overlay.querySelector('.inspection-image img');

  if (inspBrand) inspBrand.textContent = brand;
  if (inspName) inspName.textContent = name;
  if (inspPrice) inspPrice.innerHTML = '<span class="currency">ZAR</span> ' + price.replace(/[^\d.,]/g, '');
  if (inspImg) inspImg.src = imgSrc;

  generateConditionReport(overlay, brand, name);
  generateProvenance(brand, name);

  overlay.classList.add('active');
  document.body.style.overflow = 'hidden';
}

function generateConditionReport(overlay, brand, name) {
  let hash = 0;
  const str = brand + name;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  const seed = Math.abs(hash);

  const ratings = ['8/10', '8.5/10', '9/10', '9.5/10', '10/10'];
  const overalls = ['Very Good', 'Excellent', 'Excellent', 'Near Mint', 'Mint'];
  const ri = seed % ratings.length;

  const fabricConditions = [
    'No pilling, no stains, original texture intact',
    'Minimal wear, color saturation preserved',
    'Factory-fresh hand feel, no visible wear',
    'Light patina consistent with age, no damage',
    'Pristine condition, all original finishing present'
  ];
  const hardwareConditions = [
    'All original, no tarnishing, full function',
    'Original hardware, light patina, fully functional',
    'Factory-original, excellent luster maintained',
    'All zippers, snaps tested — perfect operation',
    'Original branded hardware intact, no replacements'
  ];
  const tagConditions = [
    'Original tags present, legible',
    'All tags intact, serial numbers verified',
    'Care labels and brand tags present, no fading',
    'Full tag set including size and origin labels',
    'Tags intact with original attachment method'
  ];
  const stitchConditions = [
    'Factory-consistent, no repairs detected',
    'Original stitching throughout, no alterations',
    'Thread tension uniform, consistent with production era',
    'All seams secure, stitch count matches reference',
    'No re-stitching detected under magnification'
  ];
  const authMethods = [
    'Entrupy AI + Physical Inspection',
    'Real Authentication + Macro Photography',
    'Entrupy AI + Serial Verification',
    'Physical Inspection + UV Light Analysis',
    'Entrupy AI + Material Composition Test'
  ];

  const rows = overlay.querySelectorAll('.condition-row');
  if (rows.length >= 8) {
    rows[0].querySelector('.value').textContent = overalls[ri] + ' \u2014 ' + ratings[ri];
    rows[1].querySelector('.value').textContent = fabricConditions[(seed >> 3) % fabricConditions.length];
    rows[2].querySelector('.value').textContent = hardwareConditions[(seed >> 5) % hardwareConditions.length];
    rows[3].querySelector('.value').textContent = tagConditions[(seed >> 7) % tagConditions.length];
    rows[4].querySelector('.value').textContent = stitchConditions[(seed >> 9) % stitchConditions.length];
    rows[5].querySelector('.value').textContent = authMethods[(seed >> 11) % authMethods.length];
    const authId = 'GA-2026-' + String((seed % 9000) + 1000).padStart(4, '0');
    rows[6].querySelector('.value').textContent = authId;
    const provenances = ['Acquired direct, single owner', 'Consignment from verified collector', 'Estate acquisition, documented lineage', 'Direct from original retailer, deadstock', 'Private collection, Johannesburg'];
    rows[7].querySelector('.value').textContent = provenances[(seed >> 13) % provenances.length];
  }
}

function generateProvenance(brand, name) {
  let hash = 0;
  const str = brand + name;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  const seed = Math.abs(hash);

  const now = new Date();
  const listedDate = new Date(now - (seed % 30) * 86400000);
  const authDate = new Date(listedDate - ((seed % 7) + 1) * 86400000);
  const inspDate = new Date(authDate - ((seed % 5) + 1) * 86400000);
  const acqDate = new Date(inspDate - ((seed % 30) + 5) * 86400000);

  const fmt = (d) => d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });

  const authMethods = ['Entrupy AI', 'Real Authentication', 'Physical + UV', 'Serial Match', 'Material Analysis'];
  const method = authMethods[(seed >> 4) % authMethods.length];

  const provAcq = document.getElementById('provAcquired');
  const provInsp = document.getElementById('provInspected');
  const provAuth = document.getElementById('provAuthenticated');
  const provList = document.getElementById('provListed');

  if (provAcq) provAcq.textContent = fmt(acqDate);
  if (provInsp) provInsp.textContent = fmt(inspDate);
  if (provAuth) provAuth.textContent = fmt(authDate) + ' \u2014 ' + method;
  if (provList) provList.textContent = fmt(listedDate);
}

function closeInspection() {
  const overlay = document.querySelector('.inspection-overlay');
  if (!overlay) return;
  overlay.classList.remove('active');
  document.body.style.overflow = '';
}

/* ============================================
   FILTER BAR
   ============================================ */
function initFilterBar() {
  const buttons = document.querySelectorAll('.filter-btn');
  const cards = document.querySelectorAll('.product-card');

  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      buttons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const filter = btn.dataset.filter;
      cards.forEach(card => {
        if (filter === 'all' || card.dataset.category === filter) {
          card.style.display = '';
        } else {
          card.style.display = 'none';
        }
      });
    });
  });
}

/* ============================================
   SHIPPING CARD SELECTION
   ============================================ */
function initShippingCards() {
  const cards = document.querySelectorAll('.shipping-card');
  cards.forEach(card => {
    card.addEventListener('click', () => {
      cards.forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');
    });
  });
}

/* ============================================
   MOBILE NAV
   ============================================ */
function initMobileNav() {
  const toggle = document.querySelector('.nav-toggle');
  const links = document.querySelector('.nav-bar-links');

  if (!toggle || !links) return;

  toggle.addEventListener('click', () => {
    links.classList.toggle('open');
    const spans = toggle.querySelectorAll('span');
    const isOpen = links.classList.contains('open');
    spans[0].style.transform = isOpen ? 'rotate(45deg) translate(4px, 4px)' : '';
    spans[1].style.opacity = isOpen ? '0' : '';
    spans[2].style.transform = isOpen ? 'rotate(-45deg) translate(4px, -4px)' : '';
  });

  links.querySelectorAll('a').forEach(a => {
    a.addEventListener('click', () => {
      links.classList.remove('open');
      toggle.querySelectorAll('span').forEach(s => {
        s.style.transform = '';
        s.style.opacity = '';
      });
    });
  });
}

/* ============================================
   SMOOTH SCROLL FOR NAV LINKS
   ============================================ */
function initSmoothScroll() {
  const NAV_OFFSET = 70;

  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (!href || href === '#') {
        if (this.classList.contains('coming-soon-link')) {
          e.preventDefault();
          showToast('Coming soon \u2014 DM us on Instagram @garmentarchive');
        }
        return;
      }

      e.preventDefault();
      const target = document.querySelector(href);
      if (target) {
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });

        setTimeout(() => {
          history.pushState(null, '', href);
        }, 800);
      }

      // Close mobile nav if open
      const links = document.querySelector('.nav-bar-links');
      const toggle = document.querySelector('.nav-toggle');
      if (links && links.classList.contains('open')) {
        links.classList.remove('open');
        if (toggle) {
          toggle.querySelectorAll('span').forEach(s => {
            s.style.transform = '';
            s.style.opacity = '';
          });
        }
      }
    });
  });

  // Don't preventDefault on regular navigation links (non-hash)
  // These are handled natively by the browser
}

/* ============================================
   TOAST NOTIFICATION
   ============================================ */
function showToast(message) {
  const existing = document.querySelector('.ga-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'ga-toast';
  toast.textContent = message;
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add('visible');
  });

  setTimeout(() => {
    toast.classList.remove('visible');
    setTimeout(() => toast.remove(), 500);
  }, 4000);
}

/* ============================================
   CONSTELLATION VIEW
   ============================================ */
function initConstellationView() {
  const toggle = document.getElementById('constellationToggle');
  const container = document.getElementById('constellationContainer');
  const canvas = document.getElementById('constellationCanvas');
  const tooltip = document.getElementById('constellationTooltip');
  const productGrid = document.querySelector('.product-grid');

  if (!toggle || !container || !canvas || !productGrid) return;

  const ctx = canvas.getContext('2d');
  let nodes = [];
  let isActive = false;
  let animFrame = null;
  let hoveredNode = null;
  let selectedNode = null;

  let panX = 0, panY = 0;
  let isDragging = false;
  let dragStartX = 0, dragStartY = 0;

  const brandColors = {
    'BAPE': '#7CB342',
    'Balenciaga': '#E53935',
    'Chrome Hearts': '#C9A94E',
    'St\u00FCssy': '#42A5F5',
    'Gallery Dept.': '#AB47BC',
    'Fear of God': '#78909C',
    'Rick Owens': '#ECEFF1',
    'Carhartt': '#FF8F00',
    'Denim Tears': '#5C6BC0',
    'Corteiz': '#26A69A',
    'Aelfric Eden': '#EF5350',
    'Nike': '#FF7043',
    'Adidas': '#66BB6A',
    'Vintage': '#8D6E63'
  };

  const stylePairs = {
    'tops': ['bottoms', 'outerwear', 'accessories'],
    'hoodies': ['bottoms', 'accessories'],
    'outerwear': ['tops', 'bottoms'],
    'bottoms': ['tops', 'hoodies', 'outerwear'],
    'accessories': ['tops', 'hoodies']
  };

  function gatherNodes() {
    nodes = [];
    const cards = document.querySelectorAll('.product-card');
    cards.forEach((card, i) => {
      const brand = card.querySelector('.product-card-brand')?.textContent || 'Other';
      const name = card.querySelector('.product-card-name')?.textContent || '';
      const category = card.dataset.category || 'tops';
      const imgSrc = card.querySelector('.product-card-image img')?.src || '';

      nodes.push({
        id: i, brand, name, category, imgSrc, card,
        x: 0, y: 0, vx: 0, vy: 0, targetX: 0, targetY: 0,
        radius: 6,
        color: brandColors[brand] || '#9A9A9A'
      });
    });
  }

  function layoutNodes() {
    const rect = container.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const cx = w / 2;
    const cy = h / 2;

    const brands = {};
    nodes.forEach(n => {
      if (!brands[n.brand]) brands[n.brand] = [];
      brands[n.brand].push(n);
    });

    const brandKeys = Object.keys(brands);
    const angleStep = (Math.PI * 2) / brandKeys.length;
    const outerRadius = Math.min(w, h) * 0.35;

    brandKeys.forEach((brand, bi) => {
      const clusterAngle = angleStep * bi - Math.PI / 2;
      const clusterCx = cx + Math.cos(clusterAngle) * outerRadius;
      const clusterCy = cy + Math.sin(clusterAngle) * outerRadius;
      const items = brands[brand];
      const innerRadius = Math.min(60, items.length * 8);

      items.forEach((node, ni) => {
        const a = (Math.PI * 2 / items.length) * ni;
        const r = innerRadius * (0.5 + Math.random() * 0.5);
        node.targetX = clusterCx + Math.cos(a) * r;
        node.targetY = clusterCy + Math.sin(a) * r;
        node.x = cx + (Math.random() - 0.5) * 100;
        node.y = cy + (Math.random() - 0.5) * 100;
      });
    });
  }

  function getConnections() {
    const connections = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        if (a.brand === b.brand) {
          connections.push({ from: i, to: j, type: 'brand', strength: 0.4 });
        }
        if (a.category === b.category && a.brand !== b.brand) {
          if (Math.random() < 0.08) {
            connections.push({ from: i, to: j, type: 'category', strength: 0.15 });
          }
        }
        if (stylePairs[a.category]?.includes(b.category) && a.brand !== b.brand) {
          if (Math.random() < 0.02) {
            connections.push({ from: i, to: j, type: 'style', strength: 0.08 });
          }
        }
      }
    }
    return connections;
  }

  let connections = [];
  let animProgress = 0;

  function drawConstellation() {
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    animProgress = Math.min(1, animProgress + 0.015);
    const ease = 1 - Math.pow(1 - animProgress, 3);

    nodes.forEach(n => {
      n.x += (n.targetX - n.x) * 0.06;
      n.y += (n.targetY - n.y) * 0.06;
    });

    connections.forEach(conn => {
      const a = nodes[conn.from];
      const b = nodes[conn.to];
      let color, width;
      if (conn.type === 'brand') {
        color = 'rgba(201,169,78,' + (0.12 * ease) + ')';
        width = 0.5;
      } else if (conn.type === 'category') {
        color = 'rgba(107,26,26,' + (0.2 * ease) + ')';
        width = 0.3;
      } else {
        color = 'rgba(154,154,154,' + (0.08 * ease) + ')';
        width = 0.2;
      }
      if (hoveredNode !== null && (conn.from === hoveredNode || conn.to === hoveredNode)) {
        if (conn.type === 'brand') color = 'rgba(201,169,78,0.6)';
        else if (conn.type === 'category') color = 'rgba(107,26,26,0.5)';
        else color = 'rgba(154,154,154,0.3)';
        width *= 3;
      }
      ctx.beginPath();
      ctx.moveTo(a.x + panX, a.y + panY);
      ctx.lineTo(b.x + panX, b.y + panY);
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.stroke();
    });

    nodes.forEach((n, i) => {
      const x = n.x + panX;
      const y = n.y + panY;
      const isHovered = hoveredNode === i;
      const isSelected = selectedNode === i;
      const isConnected = hoveredNode !== null && connections.some(
        c => (c.from === hoveredNode && c.to === i) || (c.to === hoveredNode && c.from === i)
      );

      let radius = n.radius * ease;
      let alpha = 0.7 * ease;

      if (isHovered || isSelected) { radius = 10; alpha = 1; }
      else if (isConnected) { radius = 8; alpha = 0.9; }
      else if (hoveredNode !== null) { alpha = 0.25; }

      if (isHovered || isSelected || isConnected) {
        ctx.beginPath();
        ctx.arc(x, y, radius + 8, 0, Math.PI * 2);
        const glow = ctx.createRadialGradient(x, y, radius, x, y, radius + 8);
        glow.addColorStop(0, n.color.replace(')', ',0.2)').replace('rgb', 'rgba'));
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = n.color;
      ctx.globalAlpha = alpha;
      ctx.fill();
      ctx.globalAlpha = 1;

      if (isHovered) {
        ctx.font = '600 9px Inter, sans-serif';
        ctx.fillStyle = n.color;
        ctx.textAlign = 'center';
        ctx.fillText(n.brand.toUpperCase(), x, y - radius - 10);
      }
    });

    if (isActive) animFrame = requestAnimationFrame(drawConstellation);
  }

  function resizeCanvas() {
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function findNodeAt(mx, my) {
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i];
      const dx = (n.x + panX) - mx;
      const dy = (n.y + panY) - my;
      if (dx * dx + dy * dy < 144) return i;
    }
    return null;
  }

  canvas.addEventListener('mousemove', (e) => {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    if (isDragging) {
      panX = mx - dragStartX;
      panY = my - dragStartY;
      return;
    }

    const found = findNodeAt(mx, my);
    hoveredNode = found;

    if (found !== null) {
      const n = nodes[found];
      canvas.style.cursor = 'pointer';
      const ttBrand = tooltip.querySelector('.constellation-tooltip-brand');
      const ttName = tooltip.querySelector('.constellation-tooltip-name');
      const ttCat = tooltip.querySelector('.constellation-tooltip-cat');
      if (ttBrand) ttBrand.textContent = n.brand;
      if (ttName) ttName.textContent = n.name;
      if (ttCat) ttCat.textContent = n.category;
      tooltip.style.left = (n.x + panX + 15) + 'px';
      tooltip.style.top = (n.y + panY - 10) + 'px';
      tooltip.classList.add('visible');
    } else {
      canvas.style.cursor = 'grab';
      tooltip.classList.remove('visible');
    }
  });

  canvas.addEventListener('mousedown', (e) => {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const found = findNodeAt(mx, my);

    if (found !== null) {
      selectedNode = found;
      openInspection(nodes[found].card);
    } else {
      isDragging = true;
      dragStartX = mx - panX;
      dragStartY = my - panY;
      canvas.style.cursor = 'grabbing';
    }
  });

  canvas.addEventListener('mouseup', () => {
    isDragging = false;
    canvas.style.cursor = hoveredNode !== null ? 'pointer' : 'grab';
  });

  canvas.addEventListener('mouseleave', () => {
    hoveredNode = null;
    isDragging = false;
    tooltip.classList.remove('visible');
  });

  toggle.addEventListener('click', () => {
    isActive = !isActive;
    toggle.classList.toggle('active', isActive);
    container.classList.toggle('active', isActive);

    if (isActive) {
      productGrid.style.display = 'none';
      setTimeout(() => {
        const containerHeight = container.getBoundingClientRect().height;
        if (containerHeight < 100) {
          container.style.height = '80vh';
        }
        setTimeout(() => {
          resizeCanvas();
          gatherNodes();
          layoutNodes();
          connections = getConnections();
          animProgress = 0;
          panX = 0;
          panY = 0;
          drawConstellation();
        }, 50);
      }, 400);
    } else {
      productGrid.style.display = '';
      if (animFrame) cancelAnimationFrame(animFrame);
      tooltip.classList.remove('visible');
    }
  });

  window.addEventListener('resize', () => {
    if (isActive) {
      resizeCanvas();
      layoutNodes();
    }
  });
}

/* ============================================
   GRAVIMETRIC SCROLL SYSTEM
   ============================================ */
function initGravimetricScroll() {
  const indicator = document.getElementById('velocityIndicator');
  const bars = indicator ? indicator.querySelectorAll('.velocity-bar') : [];
  const label = indicator ? indicator.querySelector('.velocity-label') : null;

  let lastScrollY = window.scrollY;
  let lastTime = performance.now();
  let velocity = 0;
  let smoothVelocity = 0;
  let idleTimer = null;
  let pulsedCard = null;
  let isInArchive = false;
  let ticking = false;

  const archiveFloor = document.getElementById('archive-floor');

  function checkArchiveVisibility() {
    if (!archiveFloor) return;
    const rect = archiveFloor.getBoundingClientRect();
    isInArchive = rect.top < window.innerHeight && rect.bottom > 0;
  }

  function clearPulse() {
    if (pulsedCard) {
      pulsedCard.classList.remove('patience-pulse');
      pulsedCard = null;
    }
  }

  function findNearestCard() {
    const cards = document.querySelectorAll('.product-card');
    let closest = null;
    let closestDist = Infinity;
    const viewCenter = window.innerHeight / 2;

    cards.forEach(card => {
      if (card.style.display === 'none') return;
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;
      const dist = Math.abs(cardCenter - viewCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = card;
      }
    });

    return closest;
  }

  function updateVelocityIndicator(level) {
    if (!indicator) return;
    bars.forEach((bar, i) => {
      bar.classList.toggle('lit', i < level);
    });
    if (level > 0) {
      indicator.classList.add('visible');
    }
    if (label) {
      if (level === 0) label.textContent = 'Still';
      else if (level <= 2) label.textContent = 'Slow';
      else if (level <= 5) label.textContent = 'Browse';
      else label.textContent = 'Rush';
    }
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        const now = performance.now();
        const dt = now - lastTime;
        const dy = Math.abs(window.scrollY - lastScrollY);

        velocity = (dy / Math.max(dt, 1)) * 1000;
        smoothVelocity += (velocity - smoothVelocity) * 0.3;

        lastScrollY = window.scrollY;
        lastTime = now;

        checkArchiveVisibility();

        if (!isInArchive) {
          document.body.classList.remove('scroll-fast', 'scroll-medium', 'scroll-slow');
          updateVelocityIndicator(0);
          indicator?.classList.remove('visible');
          clearPulse();
          ticking = false;
          return;
        }

        clearPulse();
        clearTimeout(idleTimer);

        if (smoothVelocity > 2000) {
          document.body.classList.add('scroll-fast');
          document.body.classList.remove('scroll-medium', 'scroll-slow');
          updateVelocityIndicator(8);
        } else if (smoothVelocity > 800) {
          document.body.classList.add('scroll-medium');
          document.body.classList.remove('scroll-fast', 'scroll-slow');
          updateVelocityIndicator(5);
        } else if (smoothVelocity > 200) {
          document.body.classList.add('scroll-slow');
          document.body.classList.remove('scroll-fast', 'scroll-medium');
          updateVelocityIndicator(3);
        } else {
          document.body.classList.remove('scroll-fast', 'scroll-medium', 'scroll-slow');
          updateVelocityIndicator(1);
        }

        idleTimer = setTimeout(() => {
          if (!isInArchive) return;
          document.body.classList.remove('scroll-fast', 'scroll-medium', 'scroll-slow');
          updateVelocityIndicator(0);

          const nearest = findNearestCard();
          if (nearest) {
            pulsedCard = nearest;
            nearest.classList.add('patience-pulse');
          }

          setTimeout(() => {
            indicator?.classList.remove('visible');
          }, 1500);
        }, 2000);

        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  setInterval(() => {
    smoothVelocity *= 0.85;
  }, 100);
}

/* ============================================
   AMBIENT DUST PARTICLES
   ============================================ */
function initDustParticles() {
  const canvas = document.getElementById('dustCanvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  const particles = [];
  const PARTICLE_COUNT = 30;

  function resize() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }
  resize();
  window.addEventListener('resize', resize);

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 1.5 + 0.5,
      speedY: -(Math.random() * 0.3 + 0.1),
      driftPhase: Math.random() * Math.PI * 2,
      driftSpeed: Math.random() * 0.005 + 0.002,
      driftAmp: Math.random() * 15 + 5,
      alpha: Math.random() * 0.2 + 0.1
    });
  }

  function animate() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particles.forEach(p => {
      p.y += p.speedY;
      p.driftPhase += p.driftSpeed;
      const driftX = Math.sin(p.driftPhase) * p.driftAmp;

      if (p.y < -10) {
        p.y = canvas.height + 10;
        p.x = Math.random() * canvas.width;
      }

      ctx.beginPath();
      ctx.arc(p.x + driftX, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(201,169,78,' + p.alpha + ')';
      ctx.fill();
    });

    requestAnimationFrame(animate);
  }

  animate();
}

/* ============================================
   DEAD STOCK ALERT NOTIFICATION
   ============================================ */
function initDeadStockAlert() {
  const alert = document.getElementById('deadstockAlert');
  const closeBtn = document.getElementById('deadstockClose');
  const timerEl = document.getElementById('deadstockTimer');
  if (!alert || !closeBtn) return;

  // Try to get product names from the page first
  const pageProducts = [];
  document.querySelectorAll('.product-card').forEach(card => {
    const brand = card.querySelector('.product-card-brand')?.textContent || '';
    const name = card.querySelector('.product-card-name')?.textContent || '';
    if (brand && name) {
      pageProducts.push(brand + ' ' + name);
    }
  });

  const pieceNames = pageProducts.length > 0 ? pageProducts : [
    'BAPE Shark Full Zip Hoodie',
    'Chrome Hearts Cross Patch',
    'Balenciaga Tracksuit Jacket',
    'Rick Owens Geobaskets',
    'Corteiz Alcatraz Hoodie',
    'Gallery Dept. Painted Denim',
    'Fear of God Essentials Hoodie',
    'Stussy Football Jersey',
    'Denim Tears Cotton Wreath',
    'Carhartt WIP Active Jacket'
  ];

  let countdownSeconds = 24 * 3600;
  let countdownInterval = null;
  let dismissTimeout = null;
  let alertIndex = 0;

  function formatTime(secs) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  }

  function showAlert() {
    const titleEl = alert.querySelector('.deadstock-title');
    if (titleEl) {
      titleEl.textContent = pieceNames[alertIndex % pieceNames.length] + ' has entered the Archive.';
    }
    alertIndex++;

    countdownSeconds = 24 * 3600;
    if (timerEl) timerEl.textContent = formatTime(countdownSeconds);

    clearInterval(countdownInterval);
    countdownInterval = setInterval(() => {
      countdownSeconds--;
      if (countdownSeconds <= 0) countdownSeconds = 0;
      if (timerEl) timerEl.textContent = formatTime(countdownSeconds);
    }, 1000);

    alert.classList.add('visible');

    clearTimeout(dismissTimeout);
    dismissTimeout = setTimeout(() => {
      hideAlert();
    }, 8000);
  }

  function hideAlert() {
    alert.classList.remove('visible');
    clearInterval(countdownInterval);
    clearTimeout(dismissTimeout);
  }

  closeBtn.addEventListener('click', hideAlert);

  setTimeout(showAlert, 10000);

  setInterval(() => {
    showAlert();
  }, 60000);
}

/* ============================================
   AUTHENTICATION DUEL GAME
   ============================================ */
function initAuthDuel() {
  const btnA = document.getElementById('duelBtnA');
  const btnB = document.getElementById('duelBtnB');
  const resultEl = document.getElementById('duelResult');
  const scoreEl = document.getElementById('duelScore');
  const nextBtn = document.getElementById('duelNext');
  const specA = document.getElementById('duelSpecimenA');
  const specB = document.getElementById('duelSpecimenB');

  if (!btnA || !btnB || !resultEl || !scoreEl || !nextBtn || !specA || !specB) return;

  const allImages = [];
  document.querySelectorAll('.product-card').forEach(card => {
    const img = card.querySelector('.product-card-image img');
    const name = card.querySelector('.product-card-name')?.textContent || '';
    if (img && img.src) {
      allImages.push({ src: img.src, name: name });
    }
  });

  if (allImages.length < 2) return;

  let correct = 0;
  let total = 0;
  let genuineAnswer = null;

  const forensicExplanations = [
    'The stitching pattern on the counterfeit shows inconsistent thread tension at the collar seam \u2014 a telltale sign of unauthorized reproduction.',
    'Under magnification, the logo embroidery on the replica reveals a thread count deviation of 12% from the authenticated reference sample.',
    'The hardware finish on the counterfeit displays micro-pitting inconsistent with factory-applied plating, suggesting aftermarket treatment.',
    'Fabric grain analysis reveals the replica uses a looser weave pattern (18 TPI vs. the standard 22 TPI), compromising structural integrity.',
    'The care label typography on the counterfeit uses a sans-serif variant where the genuine article employs a proprietary serif typeface.',
    'UV fluorescence testing reveals the counterfeit\'s tag adhesive fluoresces blue-white, while genuine articles show a characteristic amber glow.',
    'The bar-tack reinforcement at stress points on the replica is machine-applied in a straight line, whereas genuine production uses a slight arc pattern.',
    'Color spectrometry shows the dye composition of the counterfeit deviates by 3 Pantone shades from the authenticated production run.',
  ];

  function loadRound() {
    const shuffled = [...allImages].sort(() => Math.random() - 0.5);
    const imgA = shuffled[0];
    const imgB = shuffled[1] || shuffled[0];

    specA.querySelector('img').src = imgA.src;
    specA.querySelector('img').alt = imgA.name;
    specB.querySelector('img').src = imgB.src;
    specB.querySelector('img').alt = imgB.name;

    genuineAnswer = Math.random() < 0.5 ? 'A' : 'B';

    resultEl.innerHTML = '';
    btnA.disabled = false;
    btnB.disabled = false;
    nextBtn.style.display = 'none';
  }

  function handleGuess(guess) {
    total++;
    const isCorrect = guess === genuineAnswer;
    if (isCorrect) correct++;

    const explanation = forensicExplanations[Math.floor(Math.random() * forensicExplanations.length)];

    if (isCorrect) {
      resultEl.innerHTML = '<span class="correct">CORRECT.</span> Specimen ' + genuineAnswer + ' is genuine. ' + explanation;
    } else {
      resultEl.innerHTML = '<span class="incorrect">INCORRECT.</span> Specimen ' + genuineAnswer + ' was genuine. ' + explanation;
    }

    scoreEl.textContent = 'YOUR RECORD: ' + correct + '/' + total;
    btnA.disabled = true;
    btnB.disabled = true;
    nextBtn.style.display = '';
  }

  btnA.addEventListener('click', () => handleGuess('A'));
  btnB.addEventListener('click', () => handleGuess('B'));
  nextBtn.addEventListener('click', loadRound);

  loadRound();
}

/* ============================================
   CART SYSTEM (localStorage fallback for non-Shopify pages)
   ============================================ */
let cart = [];

function loadCart() {
  try {
    const saved = localStorage.getItem('garment-archive-cart');
    if (saved) cart = JSON.parse(saved);
  } catch(e) { cart = []; }
}

function saveCart() {
  try {
    localStorage.setItem('garment-archive-cart', JSON.stringify(cart));
  } catch(e) {}
}

function addToCart(item) {
  if (cart.find(c => c.id === item.id)) {
    openCartDrawer();
    return;
  }
  cart.push(item);
  saveCart();
  updateCartBadge();
  renderCart();
  openCartDrawer();
}

function removeFromCart(id) {
  cart = cart.filter(c => c.id !== id);
  saveCart();
  updateCartBadge();
  renderCart();
}

function updateCartBadge() {
  const badge = document.getElementById('cartBadge');
  if (!badge) return;
  badge.textContent = cart.length;
  badge.classList.toggle('has-items', cart.length > 0);
}

function renderCart() {
  const container = document.getElementById('cartDrawerItems');
  const totalEl = document.getElementById('cartTotal');
  const footerEl = document.getElementById('cartDrawerFooter');
  if (!container) return;

  if (cart.length === 0) {
    container.innerHTML = '<p class="cart-empty-msg">Your archive is empty.</p>';
    if (footerEl) footerEl.style.display = 'none';
    return;
  }

  if (footerEl) footerEl.style.display = '';

  let html = '';
  let total = 0;
  cart.forEach(item => {
    const priceNum = parseFloat((item.price || '').replace(/[^\d.]/g, ''));
    if (!isNaN(priceNum)) total += priceNum;

    html += '<div class="cart-item" data-cart-id="' + item.id + '">' +
      '<img class="cart-item-thumb" src="' + item.imgSrc + '" alt="' + item.name + '">' +
      '<div class="cart-item-info">' +
        '<div class="cart-item-brand">' + item.brand + '</div>' +
        '<div class="cart-item-name">' + item.name + '</div>' +
        '<div class="cart-item-price">' + (item.price || 'DM for Price') + '</div>' +
      '</div>' +
      '<button class="cart-item-remove" data-remove-id="' + item.id + '">&times;</button>' +
    '</div>';
  });

  container.innerHTML = html;

  if (totalEl) {
    totalEl.textContent = total > 0 ? 'R ' + total.toFixed(2) : 'DM for Price';
  }

  container.querySelectorAll('.cart-item-remove').forEach(btn => {
    btn.addEventListener('click', () => {
      removeFromCart(btn.dataset.removeId);
    });
  });
}

function openCartDrawer() {
  const drawer = document.getElementById('cartDrawer');
  const overlay = document.getElementById('cartOverlay');
  if (drawer) drawer.classList.add('open');
  if (overlay) overlay.classList.add('active');
}

function closeCartDrawer() {
  const drawer = document.getElementById('cartDrawer');
  const overlay = document.getElementById('cartOverlay');
  if (drawer) drawer.classList.remove('open');
  if (overlay) overlay.classList.remove('active');
}

function initCart() {
  loadCart();
  updateCartBadge();
  renderCart();

  const toggleBtn = document.getElementById('cartToggle');
  const closeBtn = document.getElementById('cartDrawerClose');
  const overlay = document.getElementById('cartOverlay');
  const checkoutBtn = document.getElementById('btnCheckoutCart');

  if (toggleBtn) toggleBtn.addEventListener('click', openCartDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeCartDrawer);
  if (overlay) overlay.addEventListener('click', closeCartDrawer);

  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', () => {
      closeCartDrawer();
      // Navigate to Shopify cart page
      window.location.href = '/cart';
    });
  }
}

/* ============================================
   BUTTON HANDLERS
   ============================================ */
function initButtonHandlers() {
  const payBtn = document.querySelector('.btn-pay');
  if (payBtn) {
    payBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const existing = payBtn.parentElement.querySelector('.confirmation-msg');
      if (existing) existing.remove();

      const msg = document.createElement('div');
      msg.className = 'confirmation-msg';
      msg.textContent = 'Order submitted! You will receive a confirmation via email shortly.';
      payBtn.parentElement.appendChild(msg);

      cart = [];
      saveCart();
      updateCartBadge();
      renderCart();
    });
  }

  const verifyBtn = document.querySelector('.verify-btn');
  const verifyInput = document.querySelector('.verify-input');
  if (verifyBtn) {
    verifyBtn.addEventListener('click', () => {
      const existing = verifyBtn.closest('.verify-input-section')?.querySelector('.confirmation-msg');
      if (existing) existing.remove();

      const code = verifyInput ? verifyInput.value.trim() : '';
      const msg = document.createElement('div');
      msg.className = 'confirmation-msg';

      if (code.length > 0) {
        msg.textContent = 'Authentication ID ' + code + ' verified. Status: AUTHENTIC. Inspected and sealed by Garment Archive.';
      } else {
        msg.textContent = 'Please enter an authentication code to verify.';
      }

      verifyBtn.closest('.verify-input-section')?.appendChild(msg);
    });
  }

  const soundToggle = document.querySelector('.sound-toggle');
  if (soundToggle) {
    soundToggle.addEventListener('click', () => {
      if (soundToggle.textContent.includes('Off')) {
        soundToggle.textContent = 'Archive Atmosphere: On';
      } else {
        soundToggle.textContent = 'Archive Atmosphere: Off';
      }
    });
  }

  document.querySelectorAll('.coming-soon-link').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      showToast('Coming soon \u2014 DM us on Instagram @garmentarchive');
    });
  });
}
